import { useMutation } from '@tanstack/react-query';
import { Alert, App, Form, Modal } from 'antd';
import type { DateTime } from 'luxon';
import { istInstant, transferOfficerSchema, type OfficerDto } from '@pams/shared';
import { DatePicker } from '@/components/DatePicker';
import { OrgUnitPicker } from '@/components/OrgUnitPicker';
import { RankPicker } from '@/components/RankPicker';
import { useAuth } from '@/hooks/useAuth';
import type { ApiError } from '@/lib/api';
import { api } from '@/lib/api';
import { useZodForm } from '@/lib/zodForm';

/**
 * FR-3.1 — a transfer changes the officer's unit and/or rank AND writes a
 * posting-history row, in one transaction. That is why it is a separate action rather
 * than two fields on the edit form.
 *
 * The date picker is `@/components/DatePicker` — the LUXON-backed one. antd's own hands
 * back a Dayjs, and ESLint blocks importing it.
 *
 * ─────────────────────────────────────────────────────────────────────────────────
 *  ⚠️ [C1e §4] "APPLYING A TRANSFER FAILS WITH INVALID INPUT" — THE REQUEST NEVER LEFT
 *  THE BROWSER, AND IT WAS NEITHER OF THE TWO OBVIOUS CAUSES.
 *
 *  Not a date-only string reaching a datetime schema, and not the reverse. The date field
 *  was bound with `{...field('effectiveFrom')}`, which attaches `zodRule` — and `zodRule`
 *  parses `form.getFieldsValue(true)`, i.e. the RAW form state, in which `effectiveFrom` is
 *  a luxon `DateTime` INSTANCE. Against `z.iso.datetime()` that is
 *  `{ code: 'invalid_type', message: 'Invalid input: expected string, received DateTime' }`,
 *  which antd rendered under the field. `validateFields()` then rejected, so `onFinish`
 *  never ran and the `.toISO()` below it never executed. **A transfer with the date left
 *  blank succeeded; one with any date picked could not be submitted at all.**
 *
 *  ⚠️ THE FIX IS A BARE `Form.Item name=`, WHICH IS WHAT EVERY OTHER DATE CONTROL IN `web/`
 *  ALREADY DOES. This was the only `field()`-bound date control in the app, and that is
 *  exactly why exactly one form was broken. The general rule: a control whose form value is
 *  not the WIRE value cannot carry a rule derived from the wire schema — the conversion
 *  happens in `onFinish`, and `submit()` validates the converted payload, which is the gate
 *  that actually matters.
 *
 *  ⚠️ NO TEST COULD SEE IT. `officer.test.ts` sends a correct ISO string (the API side is
 *  genuinely fine), and `Officers.test.tsx` only ever asserted the button's enabled state —
 *  the modal had never been opened by a test. `TransferOfficerModal.test.tsx` opens it.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/**
 * ⚠️ [C1e §4a/§4b] A TRANSFER IS CAPTURED AS A DATE AND STORED AS AN INSTANT, AND THE
 * CROSSING IS `istInstant` — THE ONE THIS REPOSITORY ALREADY HAS.
 *
 * The backend model does not change: `effectiveFrom` is still `z.iso.datetime()` on the wire
 * and a `Date` in `posting_history.from_date`. What changes is that the operator picks a DAY
 * and the client composes 09:00 on that day IN IST.
 *
 * ⚠️ `new Date('2026-09-01T09:00')` WOULD USE THE RUNTIME'S LOCAL ZONE and produce a
 * different instant on any machine not set to IST — a browser in Dubai would open the
 * posting four and a half hours early, silently. `istInstant` goes through the IANA zone,
 * exactly as `starts_at` and a duty point's `reporting_at` already do, so this introduces no
 * second time model. Its own header frames it around duty dates; a transfer date is not one,
 * and the caveat it carries there — "never rolls forward" — is about midnight-spanning
 * shifts and does not apply.
 */
const TRANSFER_EFFECTIVE_TIME = '09:00';
export const TransferOfficerModal = ({
  officer,
  onClose,
  onSuccess,
}: {
  officer: OfficerDto;
  onClose: () => void;
  onSuccess: () => void;
}) => {
  const { message } = App.useApp();
  const { officer: actor } = useAuth();
  const { form, field, submit, formErrors } = useZodForm(transferOfficerSchema);

  const transfer = useMutation({
    mutationFn: (values: Record<string, unknown>) => api.post<OfficerDto>(`/officers/${officer.id}/transfer`, values),
    onSuccess: () => {
      void message.success(`${officer.name} transferred`);
      onSuccess();
      onClose();
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  return (
    <Modal
      open
      title={`Transfer ${officer.name}`}
      okText="Transfer"
      confirmLoading={transfer.isPending}
      onCancel={onClose}
      onOk={() => form.submit()}
    >
      <Alert
        type="info"
        showIcon
        className="mb-4"
        message="This writes a posting-history record"
        description={`Their current posting at ${officer.orgUnit?.name ?? 'their unit'} is closed on the effective date and a new one opened. The history is what makes "where were they in March" answerable.`}
      />

      {formErrors.length > 0 && <Alert type="error" showIcon className="mb-4" message={formErrors.join(' ')} />}

      <Form
        form={form}
        layout="vertical"
        initialValues={{ rankId: officer.rankId }}
        onFinish={(values: { orgUnitId?: string; rankId?: string; effectiveFrom?: DateTime }) => {
          const parsed = submit({
            ...values,
            /*
             * ⚠️ THE DAY THE OPERATOR SAW, then 09:00 on it IN IST. `toISODate()` reads the
             * calendar date off the picker's own `DateTime`, which is the date rendered in
             * the field; `istInstant` turns that into the instant, and `toISOString()` into
             * the `Z`-terminated form `z.iso.datetime()` accepts (it rejects a numeric
             * offset, which is what a bare `.toISO()` on a local `DateTime` produces).
             */
            effectiveFrom: values.effectiveFrom
              ? istInstant(values.effectiveFrom.toISODate() ?? '', TRANSFER_EFFECTIVE_TIME).toISOString()
              : undefined,
          });
          if (parsed.ok) transfer.mutate(parsed.data);
        }}
      >
        <Form.Item {...field('orgUnitId')} label="Transfer to">
          {/* [C5c §11c] The level an elevated caller acts at is visible and never a destination. */}
          <OrgUnitPicker
            disabledIds={[officer.orgUnitId, ...(actor?.actingOrgUnit ? [actor.actingOrgUnit.id] : [])]}
            placeholder="Select the destination unit"
          />
        </Form.Item>

        <Form.Item {...field('rankId')} label="Rank" tooltip="Leave as-is to keep their current rank.">
          <RankPicker />
        </Form.Item>

        {/*
          ⚠️ [C1e §4] A BARE `name`, NOT `{...field(...)}` — see the header. The value here is a
          luxon `DateTime` and the schema wants a string, so a rule derived from the schema can
          only ever refuse it. `submit()` in `onFinish` validates the CONVERTED payload, which
          is the check that was always doing the real work.

          ⚠️ AND NO `showTime`. §4a captures a DATE; the hour is the fixed 09:00 IST above, so
          offering a time picker would let an operator set an hour the payload then discards.
        */}
        <Form.Item
          name="effectiveFrom"
          label="Effective from"
          tooltip="Leave it empty to transfer with effect from now. A date you pick takes effect at 09:00."
        >
          <DatePicker className="w-full" format="dd LLL yyyy" />
        </Form.Item>
      </Form>
    </Modal>
  );
};
